import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Row, Col, Alert, Typography, Checkbox, Card, Form, Input, Select, Divider, Button, Spin, Space, message, Radio } from 'antd';
import LogoWebpay from '../../images/logo-webpay.png';
import { FileOutlined, LockOutlined, CreditCardOutlined } from '@ant-design/icons';
import { REGIONS, COMMERCIAL_BUSINESS } from '../../config/constants/DropDownForms';
import rutValidator from '../../helpers/rutValidator';
import { CREATE_CONTRACT } from '../../state/lease_contract/types';
import { GET_WEBPAY_TOKEN } from '../../state/webpay/types';
import WebPayButton from '../webpay/WebPayButton';
import { PHONE_PREFIX } from '../../config/constants/forms';
import Env from '../../config/environment';
import ModalTermsAndConditions from '../modals/ModalTermsAndConditions';
import { RESOURCE } from '../../config/constants/alerts';
import Alerts from '../alerts/Alerts';
import { CONSUME_COUPON, VALIDATE_COUPON, COUPON_IS_VALID, COUPON_IS_CONSUMED } from '../../state/coupon/types';
import { STATUS } from '../../config/constants/states';
import PropTypes from 'prop-types';
import Schema from '../../classes/index';
import ReactFlagsSelect from 'react-flags-select';
import { trackEvent } from '../../helpers/analytics';

const env = Env();
const webpayUrl = env.webpay.url;
const { Title, Text } = Typography;
const { Option, OptGroup } = Select;

const Payment = ({
  contractFormData,
  getWebpayToken,
  createContract,
  contractId,
  consumeCoupon,
  validateCoupon,
  isValid,
  discount,
  isConsumed,
  resetValidateCouponStatus,
  resetConsumeCouponStatus,
  docPrice,
  regex,
  spinner,
}) => {
  const [form] = Form.useForm();
  const [couponForm] = Form.useForm();
  const [visibleForm, setVisibleForm] = useState(false);
  const [autocompleteUser, setAutocompleteUser] = useState('lessor');
  const [modalVisible, setModalVisible] = useState(false);
  const [hasCoupon, setHasCoupon] = useState(false);
  const [contractPrice, setContractPrice] = useState(docPrice);
  const [couponIsValid, setCouponIsValid] = useState(false);
  const [couponData, setCouponData] = useState({});
  const [resumeOwnerData, setResumeOwnerData] = useState({ name: '', email: '', rut: '' });
  const [selected, setSelected] = useState('CL');
  const [usedRut, setUsedRut] = useState('');

  const webpayPaymentData = () => {
    getWebpayToken({
      buyOrder: contractId,
      sessionId: contractId,
      amount: contractPrice,
      returnUrl: webpayUrl,
      currency: 'CLP',
      rut: usedRut
    });
  };

  //Send data from owner form
  const formContent = async (data) => {
    trackEvent('Payment', 'payment_initiated', 'Confirmar y pagar', contractPrice);
    sessionStorage.setItem('ownerMail', data.webpay_email);
    createContract(await Schema.rentContract.create({
      ...contractFormData,
      ...data,
    }));
  };

  //when the saga tries to create a contract
  useEffect(() => {
    if (couponIsValid && contractId !== '') {
      consumeCoupon({
        code: couponData.coupon_code,
        rut: couponData.coupon_rut,
        id: contractId
      });
    }
    if (!couponIsValid && contractId !== '' && isConsumed !== STATUS.ERROR) {
      webpayPaymentData();
    }
  }, [contractId]);

  useEffect(() => {
    if (isConsumed === STATUS.SUCCESS && contractId !== '') {
      webpayPaymentData();
    }
    if (isConsumed === STATUS.ERROR) {
      message.error('Hubo un problema con tu cupón de descuento', 10);
      setContractPrice(contractPrice);
      setCouponIsValid(false);
      resetConsumeCouponStatus();
    }
  }, [isConsumed]);

  //Send data from coupon from
  const verifyCoupon = (data) => {
    setCouponData(data);
    validateCoupon({
      code: data.coupon_code,
      rut: data.coupon_rut
    });
  };

  //when the saga tries to validate coupon
  useEffect(() => {
    if (isValid === STATUS.SUCCESS && !couponIsValid) {
      setCouponIsValid(true);
      resetValidateCouponStatus();
      if (discount.percentageOff) {
        setContractPrice(contractPrice - (contractPrice * (discount.percentageOff / 100)));
      }
      if (discount.amountOff) {
        setContractPrice(contractPrice - discount.amountOff);
      }
    }
    if (isValid === STATUS.ERROR) {
      message.error('Cupón no válido', 10);
      resetValidateCouponStatus();
    }
  }, [isValid]);

  useEffect(() => {
    if (visibleForm && autocompleteUser) {
      form?.setFieldsValue({
        webpay_nameCompany: contractFormData[autocompleteUser][0]?.businessName,
        webpay_giro: contractFormData[autocompleteUser][0]?.giro,
        webpay_rut: contractFormData[autocompleteUser][0]?.companyRol,
        webpay_address: contractFormData[autocompleteUser][0]?.address,
        webpay_city: contractFormData[autocompleteUser][0]?.commune,
        webpay_name: `${contractFormData[autocompleteUser][0]?.firstName} ${contractFormData[autocompleteUser][0]?.lastName}`,
        webpay_email: contractFormData[autocompleteUser][0]?.email,
        webpay_phone: contractFormData[autocompleteUser][0]?.phoneNumber,
        webpay_phone_prefix: contractFormData[autocompleteUser][0]?.phonePrefix,
      });
      setSelected(Object.keys(PHONE_PREFIX).find(key => PHONE_PREFIX[key].secondary === contractFormData[autocompleteUser][0]?.phonePrefix));
      setUsedRut(contractFormData[autocompleteUser][0]?.companyRol);
    } else if (autocompleteUser) {
      form?.setFieldsValue({
        webpay_name: `${contractFormData[autocompleteUser][0]?.firstName} ${contractFormData[autocompleteUser][0]?.lastName}`,
        webpay_rut: contractFormData[autocompleteUser][0]?.rut,
        webpay_address: contractFormData[autocompleteUser][0]?.address,
        webpay_city: contractFormData[autocompleteUser][0]?.commune,
        webpay_email: contractFormData[autocompleteUser][0]?.email,
        webpay_phone: contractFormData[autocompleteUser][0]?.phoneNumber,
        webpay_phone_prefix: contractFormData[autocompleteUser][0]?.phonePrefix
      });
      setSelected(Object.keys(PHONE_PREFIX).find(key => PHONE_PREFIX[key].secondary === contractFormData[autocompleteUser][0]?.phonePrefix));
      setUsedRut(contractFormData[autocompleteUser][0]?.rut);
    }
    else {
      setSelected('CL');
      form.resetFields();
    }
  }, [autocompleteUser, visibleForm]);

  useEffect(() => {
    if (autocompleteUser) setVisibleForm(contractFormData[autocompleteUser][0]?.hasLegalPerson ?? false);
  }, [autocompleteUser]);

  const validateCheck = (e) => {
    return e ? true : false;
  };

  const acceptConditions = () => {
    form.setFieldsValue({ webpay_acceptConditions: true });
    setModalVisible(false);
  };

  useEffect(() => {
    if (autocompleteUser) {
      setResumeOwnerData({
        name: `${contractFormData[autocompleteUser][0]?.firstName} ${contractFormData[autocompleteUser][0]?.lastName}`,
        email: contractFormData[autocompleteUser][0]?.email,
        rut: contractFormData[autocompleteUser][0]?.rut
      });
    } else {
      setResumeOwnerData({ name: '', email: '', rut: '' });
    }
  }, [autocompleteUser]);

  const handlerPrefix = (code) => {
    form.setFieldsValue({ webpay_phone_prefix: PHONE_PREFIX[code]?.secondary });
  };

  const openInNewWindow = () => {
    window.open('https://www.legalchile.cl/terminos-y-condiciones/', '_blank', 'noopener,noreferrer,width=800,height=600');
  };

  return (
    <Spin
      spinning={spinner}
      tip="Procesando solicitud..."
    >
      <WebPayButton />
      <Alerts />
      <Row gutter={16} className="justify-content-center">
        <Col xs={24} md={10} className="mb-4">
          <Form
            form={form}
            layout="vertical"
            onFinish={formContent}
            autoComplete="on"
          >
            <Card className="p-2">
              <div className="mb-4">
                <Title level={3} className="text-purple">Detalles de facturación</Title>
                <Alert message="Los datos que ingreses en este paso, serán también los del creador del documento. Si algún firmante objeta el contrato notificaremos al correo ingresado en este momento." className="mb-2" />
                <Title level={5}>Selecciona el usuario</Title>
                <Radio.Group className="d-flex flex-column"
                  onChange={(option) => { setAutocompleteUser(option.target.value); }}
                  value={autocompleteUser}
                >
                  <Radio value="lessor">Usar datos del arrendador</Radio>
                  <Radio value="lessee">Usar datos del arrendatario</Radio>
                  <Radio value={false}>Ingresar datos manualmente</Radio>
                </Radio.Group>
              </div>
              <div className="mb-4">
                <Title level={5}>¿Eres una empresa?</Title>
                <Radio.Group
                  onChange={(option) => { setVisibleForm(option.target.value); }}
                  value={visibleForm}
                >
                  <Radio value={true}>Si</Radio>
                  <Radio value={false}>No</Radio>
                </Radio.Group>
              </div>
              {visibleForm &&
                <>
                  <Title level={4}>Datos de empresa</Title>
                  <Form.Item
                    label="Razon social"
                    name="webpay_nameCompany"
                    rules={[
                      ({
                        validator(_, value) {
                          if (value !== undefined && regex.test(value)) {
                            return Promise.reject(
                              'No debe usar simbolos especiales.',
                            );
                          }
                          return Promise.resolve();
                        },
                      }),
                      { required: true, message: 'Debes ingresar un nombre' }
                    ]}
                  >
                    <Input size="large" />
                  </Form.Item>
                  <Form.Item
                    label="Giro"
                    name="webpay_giro"
                    rules={[{ required: true, message: 'Debes seleccionar un giro' }]}
                  >
                    <Select size="large" showSearch style={{ width: '100%' }} placeholder="Seleccionar" initialvalues='Sin giro' >
                      <Option value='Sin giro'>Sin giro</Option>
                      {COMMERCIAL_BUSINESS.map((giro, key) => (
                        <OptGroup key={key} label={giro.nombre}>
                          {giro.giros.map((nombreGiro, key) => (
                            <Option key={key} value={nombreGiro}>{nombreGiro}</Option>
                          ))}
                        </OptGroup>
                      ))}
                    </Select>
                  </Form.Item>
                </>
              }
              {!visibleForm &&
                <>
                  <Title level={4}>Datos personales</Title>
                  <Form.Item
                    label="Nombre completo"
                    name="webpay_name"
                    rules={[
                      ({
                        validator(_, value) {
                          if (value !== undefined && regex.test(value)) {
                            return Promise.reject(
                              'No debe usar simbolos especiales.',
                            );
                          }
                          return Promise.resolve();
                        },
                      }),
                      { required: true, message: 'Debes ingresar un nombre' },
                    ]}
                  >
                    <Input size="large"
                      onBlur={(event) => {
                        setResumeOwnerData({ ...resumeOwnerData, name: event.target.value });
                      }}
                    />
                  </Form.Item>
                </>
              }
              <Form.Item
                label="RUT"
                name="webpay_rut"
                rules={[
                  ({
                    validator(_, value) {
                      if (value && rutValidator(value)) {
                        return Promise.resolve();
                      }
                      return Promise.reject(
                        'Debe ingresar un rut válido',
                      );
                    },
                  }),
                  { required: true, message: 'Debes ingresar un rut' },
                  {
                    pattern: new RegExp(/^[0-9]+[-|‐]{1}[0-9kK]{1}$/),
                    message: 'Debe ingresar rut con guión'
                  },
                ]}
              >
                <Input size="large"
                  onBlur={(event) => {
                    setResumeOwnerData({ ...resumeOwnerData, rut: event.target.value });
                    setUsedRut(event.target.value);
                  }}
                />
              </Form.Item>
              <Form.Item
                label="Dirección"
                name="webpay_address"
                rules={[
                  ({
                    validator(_, value) {
                      if (value !== undefined && regex.test(value)) {
                        return Promise.reject(
                          'No debe usar simbolos especiales.',
                        );
                      }
                      return Promise.resolve();
                    },
                  }),
                  { required: true, message: 'Debes ingresar una dirección' }
                ]}
              >
                <Input size="large" />
              </Form.Item>
              <Form.Item
                label="Comuna"
                name="webpay_city"
                rules={[{ required: true, message: 'Debes ingresar una ciudad' }]}
              >
                <Select size="large" showSearch style={{ width: '100%' }} placeholder="Ingresar o seleccionar">
                  {REGIONS.map((region, key) => (
                    <OptGroup key={key} label={region.region}>
                      {region.comunas.map((comuna, key) => (
                        <Option key={key} value={`${comuna}, ${region.region}`}>{comuna}</Option>
                      ))}
                    </OptGroup>
                  ))}
                </Select>
              </Form.Item>
              {visibleForm &&
                <>
                  <Title level={4}>Datos personales</Title>
                  <Form.Item
                    label="Nombre completo"
                    name="webpay_name"
                    rules={[
                      ({
                        validator(_, value) {
                          if (value !== undefined && regex.test(value)) {
                            return Promise.reject(
                              'No debe usar simbolos especiales.',
                            );
                          }
                          return Promise.resolve();
                        },
                      }),
                      { required: true, message: 'Debes ingresar un nombre' }
                    ]}
                  >
                    <Input size="large"
                      onBlur={(event) => {
                        setResumeOwnerData({ ...resumeOwnerData, name: event.target.value });
                      }}
                    />
                  </Form.Item>
                </>
              }
              <Title level={4}>Datos de contacto</Title>
              <Form.Item
                label="Teléfono"
                name="webpay_phone"
                rules={[
                  { required: true, pattern: new RegExp(/^[0-9]/), message: 'Debes ingresar un número válido' },
                  { min: 9, message: 'El número ingresado debe tener al menos 9 dígitos' },
                ]}
              >
                <Input
                  className="custom-addon"
                  size="large"
                  addonBefore={
                    <Form.Item
                      name='webpay_phone_prefix'
                      initialValue={PHONE_PREFIX[selected]?.secondary ?? '+56'}
                      noStyle
                    >
                      <ReactFlagsSelect
                        className="select-prefix-custom"
                        defaultCountry="CL"
                        selected={selected}
                        onSelect={code => {
                          setSelected(code);
                          handlerPrefix(code);
                        }}
                        customLabels={PHONE_PREFIX}
                      />
                    </Form.Item>
                  }
                  maxLength={100}
                />
              </Form.Item>
              <Form.Item
                label="Correo electrónico"
                name="webpay_email"
                rules={[
                  {
                    required: true,
                    pattern: new RegExp(/^\S+@\S+\.\S+$/),
                    message: 'Debes ingresar un correo válido'
                  }]}
              >
                <Input size="large"
                  onBlur={(event) => {
                    setResumeOwnerData({ ...resumeOwnerData, email: event.target.value });
                  }}
                />
              </Form.Item>
              <Form.Item
                name="webpay_confirmCheck"
                valuePropName="checked"
                rules={[
                  ({
                    validator(_, value) {
                      if (value && validateCheck(value)) {
                        return Promise.resolve();
                      }
                      return Promise.reject(
                        'Debes confirmar los datos ingresados',
                      );
                    },
                  })
                ]}
              >
                <Checkbox >Confirmo que los datos y documentos entregados son verídicos y están correctos.  *</Checkbox>
              </Form.Item>
            </Card>
          </Form>
        </Col>
        <Col xs={24} md={11}>
          <Card className="mb-5 p-2">
            <Title level={3}>Tu pedido</Title>
            <Divider />
            <div className="list-flex mb-4">
              {resumeOwnerData.name}<br />
              {resumeOwnerData.email}<br />
              {resumeOwnerData.rut}
            </div>
            <div className="list-flex">
              <p>Contrato de arriendo</p>
              <p>${contractPrice.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.')}</p>
            </div>
            <div className="icon-block">
              <FileOutlined style={{ fontSize: '24px', color: '#0000FF' }} />
            </div>
            <p className="small mb-2">Código: DCL406</p>
            <Row gutter={16}>
              <Col xs={24} md={24} className="mb-4">
                <Form
                  form={couponForm}
                  layout="vertical"
                  onFinish={verifyCoupon}
                  autoComplete="on"
                >
                  <div className="mb-4">
                    <Checkbox onChange={(value) => setHasCoupon(value.target.checked)}>Tengo un cupón de descuento</Checkbox>
                  </div>
                  {hasCoupon &&
                    <>
                      <Form.Item
                        label="Ingrese rut de beneficiario"
                        name="coupon_rut"
                        rules={[
                          ({
                            validator(_, value) {
                              if (value && rutValidator(value)) {
                                return Promise.resolve();
                              }
                              return Promise.reject(
                                'Debe ingresar un rut válido',
                              );
                            },
                          }),
                          { required: true, message: 'Debes ingresar un rut' },
                          {
                            pattern: new RegExp(/^[0-9]+[-|‐]{1}[0-9kK]{1}$/),
                            message: 'Debe ingresar rut con guión'
                          },
                        ]}
                      >
                        <Input size="large" />
                      </Form.Item>
                      <Form.Item
                        label="Código de cupón"
                        name="coupon_code"
                        rules={[{ required: true, message: 'Debes ingresar código de cupón' }]}
                      >
                        <Input size="large" />
                      </Form.Item>
                      <Col span={24} >
                        {!couponIsValid
                          ? <Space>
                            <Button size="large" type="primary" htmlType="submit">Validar</Button>
                          </Space>
                          : <>
                            <Title level={5} className="mb-4">Cupon valido!</Title>
                            <Title level={5}>Se aplicará un descuento de {discount && discount.amountOff ? `$${discount.amountOff.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.')}` : `un ${discount.percentageOff}%`}</Title>
                          </>
                        }
                      </Col>
                    </>
                  }
                </Form>
              </Col>
            </Row>
            <Row className="mb-2">
              <Col span={24}>
                <div className="list-flex">
                  <Title level={5}>Contrato de arriendo</Title>
                  {!couponIsValid
                    ? <Title level={5}>${contractPrice.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.')}</Title>
                    : <div className="text-right">
                      <Text delete>Antes: ${contractPrice.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.')}</Text>
                      <Title level={5}>Ahora: ${Math.ceil(contractPrice).toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.')}</Title>
                    </div>
                  }
                </div>
              </Col>
            </Row>
            <Divider />
            <Form
              form={form}
              layout="vertical"
              onFinish={formContent}
              autoComplete="on"
            >
              <Row className="mb-2 text-center">
                <Button type="link" onClick={openInNewWindow}>Ver terminos y condiciones</Button>
              </Row>
              <Row className="mb-2 text-center">
                <Form.Item
                  name="webpay_acceptConditions"
                  valuePropName="checked"
                  rules={[
                    ({
                      validator(_, value) {
                        if (value && validateCheck(value)) {
                          return Promise.resolve();
                        }
                        return Promise.reject(
                          'Debes aceptar los terminos y condiciones',
                        );
                      },
                    })
                  ]}
                >
                  <Checkbox>Acepto los términos y condiciones</Checkbox>
                </Form.Item>
              </Row>
              <Row className="mb-2">
                <Col span={24} className="text-center">
                  <Space>
                    <Button className="mb-4" size="large" type="primary" htmlType="submit">Confirmar y pagar</Button>
                  </Space>
                </Col>
              </Row>
              <Row>
                <Col span={24}>
                  <img src={LogoWebpay} alt="" className="d-block mx-auto" />
                </Col>
              </Row>
            </Form>
          </Card>
          <Row className="justify-content-center">
            <div className="text-center mx-3">
              <LockOutlined />
              <p className="small">Pago Seguro</p>
            </div>
            <div className="text-center mx-3">
              <CreditCardOutlined />
              <p className="small">Webpay</p>
            </div>
            <div className="text-center mx-3">
              <LockOutlined />
              <p className="small">Pago Seguro</p>
            </div>
          </Row>
        </Col>
      </Row>
      <ModalTermsAndConditions
        visible={modalVisible}
        onCancel={() => setModalVisible(false)}
        onCreate={() => acceptConditions()}
      />
    </Spin>
  );
};

Payment.propTypes = {
  contractFormData: PropTypes.object.isRequired,
  getWebpayToken: PropTypes.func.isRequired,
  createContract: PropTypes.func.isRequired,
  contractId: PropTypes.string.isRequired,
  consumeCoupon: PropTypes.func.isRequired,
  validateCoupon: PropTypes.func.isRequired,
  isValid: PropTypes.string.isRequired,
  discount: PropTypes.object.isRequired,
  isConsumed: PropTypes.string.isRequired,
  resetValidateCouponStatus: PropTypes.func.isRequired,
  resetConsumeCouponStatus: PropTypes.func.isRequired,
  docPrice: PropTypes.number.isRequired,
  regex: PropTypes.regex,
  spinner: PropTypes.bool,
};

const data = (state) => ({
  contractFormData: state.contract.contractFormData,
  contractId: state.contract.contractId,
  isValid: state.coupon.isValid,
  discount: state.coupon.discount,
  isConsumed: state.coupon.isConsumed,
  docPrice: state.contract.docPrice,
  regex: state.forms.regex,
  spinner: state.webpay.spinner,
});

const actions = (dispatch) => ({
  getWebpayToken: (data) => dispatch({ type: GET_WEBPAY_TOKEN, payload: { ...data } }),
  createContract: (payload) => dispatch({ type: CREATE_CONTRACT, payload: { payload, resource: RESOURCE.SAVE_CONTRACT } }),
  consumeCoupon: (data) => dispatch({ type: CONSUME_COUPON, payload: { ...data, resource: RESOURCE.CONSUME_COUPON } }),
  validateCoupon: (data) => dispatch({ type: VALIDATE_COUPON, payload: { ...data, resource: RESOURCE.VALIDATE_COUPON } }),
  resetValidateCouponStatus: () => dispatch({ type: COUPON_IS_VALID, payload: STATUS.PENDING }),
  resetConsumeCouponStatus: () => dispatch({ type: COUPON_IS_CONSUMED, payload: STATUS.PENDING }),
});

export default connect(data, actions)(Payment);